import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  simpleGeometryLinePreviewProperties,
  simpleGeometryPointPreviewProperties,
  simpleGeometryCirclePreviewProperties,
  simpleGeometryArcPreviewProperties,
  lwpolylinePatchableProperties,
  textPatchableProperties,
  mtextPatchableProperties,
  dimensionPatchableProperties,
  multileaderPatchableProperties,
  revisionCloudPatchableProperties,
  hatchPatchableProperties,
  blockInsertPatchableProperties,
  arrayRectPatchableProperties,
  viewportPatchableProperties,
  patchablePropertyLookup,
} from "./previewBuilders";

// Type vocabulary + low-level numeric helpers (formatNumber,
// parseNumber, pickCommonProperties, computePolyline*) live in
// `geometryEntityTypes.ts` so `previewBuilders.ts` and
// `extendedPropertyMutations.ts` can pull them in without re-importing
// this module (was the source of three depcruise cycles). Re-exported
// here so existing consumers of `simpleGeometry.ts` keep working.
export {
  formatNumber,
  parseNumber,
  pickCommonProperties,
  simpleGeometryCommonPreviewProperties,
  computePolylineLength,
  computePolylineArea,
  normalizeAngleSpan,
} from "./geometryEntityTypes";
export type {
  SimpleGeometryPoint,
  SimpleGeometryPointLike,
  SimpleLineEntity,
  SimplePointEntity,
  SimpleCircleEntity,
  SimpleArcEntity,
  SimpleEllipseEntity,
  SimpleSplineEntity,
  SimpleLwpolylineEntity,
  SimpleTextEntity,
  SimpleMtextEntity,
  SimpleDimensionEntity,
  SimpleMultileaderEntity,
  SimpleRevisionCloudEntity,
  SimpleHatchEntity,
  SimpleBlockInsertEntity,
  SimpleArrayRectEntity,
  SimpleViewportEntity,
  ExtendedGeometryEntity,
  SimpleGeometryEntity,
  AllGeometryEntity,
  ExtendedGeometryPatch,
  SimpleGeometryPropertyChange,
  SimpleGeometryPropertySkip,
  SimpleGeometryPatch,
  SimpleGeometryPropertyChangeResult,
} from "./geometryEntityTypes";

import {
  formatNumber,
  normalizeAngleSpan,
  parseNumber,
  pickCommonProperties,
} from "./geometryEntityTypes";
// fix/properties-apply-derived-fields-skip — the ONE-TRUTH classification
// (typed CAD-core registry) distinguishing a KNOWN computed/display-only
// field (Length/Angle/Diameter/Circumference/Area/Total angle/Arc length —
// `read-only-calc` descriptors) from a genuine incompatibility. `simple-
// Geometry.ts` (this barrel) already transitively depends on the typed
// descriptor registry via its own `export * from "./extendedPropertyMutations"`
// (which imports `typedPropertyWriter.ts` → `typedPropertyDescriptors.ts`), so
// this direct import adds no new dep-graph edge class (ПРИОРИТЕТ #2).
import { isDerivedDisplayProperty } from "./typedPropertyDescriptors";
import type {
  SimpleGeometryPointLike,
  SimpleLineEntity,
  SimpleCircleEntity,
  SimpleArcEntity,
  SimpleEllipseEntity,
  SimpleSplineEntity,
  SimpleLwpolylineEntity,
  SimpleTextEntity,
  SimpleMtextEntity,
  SimpleDimensionEntity,
  SimpleMultileaderEntity,
  SimpleRevisionCloudEntity,
  SimpleHatchEntity,
  SimpleBlockInsertEntity,
  SimpleArrayRectEntity,
  SimpleViewportEntity,
  SimpleGeometryEntity,
  SimpleGeometryPatch,
  SimpleGeometryPropertyChange,
  SimpleGeometryPropertySkip,
  SimpleGeometryPropertyChangeResult,
} from "./geometryEntityTypes";

function parseStrictNumber(value: string): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parsePositiveNumber(value: string): number | null {
  const parsed = parseStrictNumber(value);
  if (parsed === null || parsed <= 0) {
    return null;
  }

  return parsed;
}

// `normalizeAngleSpan` RELOCATED to `geometryEntityTypes.ts` (leaf) — the
// descriptors imported it from THIS barrel, which `export *`s
// `extendedPropertyMutations`, closing a runtime module cycle once the shell
// applier gained its S15 Slice A typed-writer mirror edge. Re-exported below
// (with the other geometryEntityTypes helpers) so existing consumers keep
// resolving; the local use at `deriveArcPreviewProperties` imports it directly.

function isSimpleGeometryEntity(entity: EntityPreview): entity is EntityPreview & {
  type: SimpleGeometryEntity["type"];
} {
  return entity.type === "Line" || entity.type === "Circle" || entity.type === "Arc";
}

function createSummary(entityType: SimpleGeometryEntity["type"], isUpdated: boolean): string {
  return isUpdated
    ? `Geometry properties applied to ${entityType}`
    : `Geometry properties applied to ${entityType}: no applicable property changes`;
}

export function isSimpleGeometryPatchableProperty(
  entityType: SimpleGeometryEntity["type"],
  propertyName: string
): boolean {
  return patchablePropertyLookup[entityType].has(propertyName);
}

export function deriveSimpleGeometryPreviewProperties(
  entity: SimpleGeometryEntity,
  properties: Record<string, string> = {}
): Record<string, string> {
  const baseProperties = pickCommonProperties(properties);

  switch (entity.type) {
    case "Line": {
      // DXF LINE endpoints carry an optional Z (group 30/31). Surface it for
      // AutoCAD parity (Point "Position Z" / Circle+Arc "Center Z") and so a
      // 3-D line's elevation is visible + survives a 2-D grip edit. Absent z ⇒
      // 0 by the model contract (drawingPointOptionalZSchema; the bridge emits
      // z ?? 0 on export), and a previously-edited "Start Z" string is the
      // fallback when the entity literal carried no z (residual R2 #317).
      const startZ = entity.start.z ?? parseNumber(properties["Start Z"], 0);
      const endZ = entity.end.z ?? parseNumber(properties["End Z"], 0);
      const dx = entity.end.x - entity.start.x;
      const dy = entity.end.y - entity.start.y;
      const dz = endZ - startZ;
      // AutoCAD LINE "Length" is the 3-D endpoint distance (Properties palette
      // Length = hypot(ΔX, ΔY, ΔZ)). Now that Start Z / End Z are editable, a
      // pure-Z edit (e.g. End Z 0→10 with X/Y fixed) must report the true 10-unit
      // span, not a 2-D length of 0 (residual R2 #317 — Length-3D). For a planar
      // line (dz = 0) this collapses to the previous hypot(dx, dy).
      const length = Math.hypot(dx, dy, dz);
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

      return {
        ...baseProperties,
        "Start X": formatNumber(entity.start.x),
        "Start Y": formatNumber(entity.start.y),
        "Start Z": formatNumber(startZ),
        "End X": formatNumber(entity.end.x),
        "End Y": formatNumber(entity.end.y),
        "End Z": formatNumber(endZ),
        Length: formatNumber(length),
        Angle: formatNumber(angle)
      };
    }
    case "Point": {
      const locationZ = entity.location.z ?? parseNumber(properties["Position Z"], 0);
      return {
        ...baseProperties,
        "Position X": formatNumber(entity.location.x),
        "Position Y": formatNumber(entity.location.y),
        "Position Z": formatNumber(locationZ),
        // Rotation is DXF group 50 = DEGREES — verbatim (identity like TEXT/MText).
        Rotation: formatNumber(entity.rotation)
      };
    }
    case "Circle": {
      const centerZ = entity.center.z ?? parseNumber(properties["Center Z"], 0);
      const diameter = entity.radius * 2;
      const circumference = 2 * Math.PI * entity.radius;
      const area = Math.PI * (entity.radius ** 2);

      return {
        ...baseProperties,
        "Center X": formatNumber(entity.center.x),
        "Center Y": formatNumber(entity.center.y),
        "Center Z": formatNumber(centerZ),
        Radius: formatNumber(entity.radius),
        Diameter: formatNumber(diameter),
        Circumference: formatNumber(circumference),
        Area: formatNumber(area)
      };
    }
    case "Arc": {
      const centerZ = entity.center.z ?? parseNumber(properties["Center Z"], 0);
      const totalAngle = normalizeAngleSpan(entity.startAngle, entity.endAngle);
      const arcLength = (Math.PI * entity.radius * totalAngle) / 180;

      return {
        ...baseProperties,
        "Center X": formatNumber(entity.center.x),
        "Center Y": formatNumber(entity.center.y),
        "Center Z": formatNumber(centerZ),
        Radius: formatNumber(entity.radius),
        "Start angle": formatNumber(entity.startAngle),
        "End angle": formatNumber(entity.endAngle),
        "Total angle": formatNumber(totalAngle),
        "Arc length": formatNumber(arcLength)
      };
    }
  }
}

export function buildSimpleGeometryPreviewEntity(
  entity: SimpleGeometryEntity,
  options: {
    label?: string;
    summary?: string;
    properties?: Record<string, string>;
  } = {}
): EntityPreview {
  const properties = deriveSimpleGeometryPreviewProperties(entity, options.properties ?? {});
  const editableProperties =
    entity.type === "Line"
      ? [...simpleGeometryLinePreviewProperties]
      : entity.type === "Point"
        ? [...simpleGeometryPointPreviewProperties]
        : entity.type === "Circle"
          ? [...simpleGeometryCirclePreviewProperties]
          : [...simpleGeometryArcPreviewProperties];

  return {
    id: entity.id,
    label: options.label ?? `${entity.type} ${entity.id}`,
    type: entity.type,
    spaceId: entity.spaceId,
    summary:
      options.summary ?? `Simple ${entity.type.toLowerCase()} created through the geometry command core.`,
    editableProperties,
    properties
  };
}

function readLineState(properties: Record<string, string>): SimpleLineEntity {
  // Carry the endpoint Z (group 30/31) through the read so a 2-D X/Y edit keeps
  // the existing elevation: the patch builder re-emits the full point incl. Z,
  // and `patchEntityById` shallow-merge would otherwise drop a non-zero Z
  // (residual R2 #317 — Z-preserve). `Start Z` / `End Z` are only present once
  // the Line preview surfaces them (deriveSimpleGeometryPreviewProperties); when
  // absent they default to 0, matching the model's `z ?? 0` export contract.
  const startZ = parseNumber(properties["Start Z"], 0);
  const endZ = parseNumber(properties["End Z"], 0);
  return {
    id: "",
    type: "Line",
    spaceId: "",
    start: {
      x: parseNumber(properties["Start X"], 0),
      y: parseNumber(properties["Start Y"], 0),
      z: startZ
    },
    end: {
      x: parseNumber(properties["End X"], 0),
      y: parseNumber(properties["End Y"], 0),
      z: endZ
    }
  };
}

function readCircleState(properties: Record<string, string>): SimpleCircleEntity {
  return {
    id: "",
    type: "Circle",
    spaceId: "",
    center: {
      x: parseNumber(properties["Center X"], 0),
      y: parseNumber(properties["Center Y"], 0),
      z: parseNumber(properties["Center Z"], 0)
    },
    radius: parseNumber(properties["Radius"], 0)
  };
}

function readArcState(properties: Record<string, string>): SimpleArcEntity {
  return {
    id: "",
    type: "Arc",
    spaceId: "",
    center: {
      x: parseNumber(properties["Center X"], 0),
      y: parseNumber(properties["Center Y"], 0),
      z: parseNumber(properties["Center Z"], 0)
    },
    radius: parseNumber(properties["Radius"], 0),
    startAngle: parseNumber(properties["Start angle"], 0),
    endAngle: parseNumber(properties["End angle"], 0)
  };
}

/**
 * 3-D point equality including Z (group 30/31). Used by the Line edit path so a
 * pure "Start Z" / "End Z" change registers as a real change AND so the patch
 * builder re-emits the full point (incl. Z) — `patchEntityById` shallow-merges
 * `start` / `end`, so re-emitting Z is what preserves a non-zero elevation
 * across a 2-D X/Y edit (residual R2 #317). A `z?` field defaults to 0 (model
 * `drawingPointOptionalZSchema` + export `z ?? 0`), so `undefined` Z compares
 * equal to 0 — a 2-D literal and an explicit z:0 are the same elevation.
 */
function isPointEqualXYZ(
  left: { x: number; y: number; z?: number },
  right: { x: number; y: number; z?: number }
): boolean {
  return (
    left.x === right.x &&
    left.y === right.y &&
    (left.z ?? 0) === (right.z ?? 0)
  );
}

/**
 * Build a patch endpoint that carries Z ONLY when it is a non-zero elevation.
 * A z of 0 (or absent) is the default planar value (the model field is optional
 * and export does `z ?? 0`), so omitting it keeps a 2-D line's bare `{x,y}`
 * shape — re-emitting `z:0` would add a spurious field and break a
 * byte-identical round-trip. A NON-ZERO z is preserved verbatim so the shallow
 * merge in `patchEntityById` cannot flatten an elevated line (residual R2 #317).
 */
function withOptionalZ(
  x: number,
  y: number,
  z: number | undefined
): SimpleGeometryPointLike {
  return z !== undefined && z !== 0 ? { x, y, z } : { x, y };
}

/**
 * Apply a single CENTER axis change ("Center X" / "Center Y" / "Center Z") to a
 * mutable center point, in place. Returns `"applied"` when the value actually
 * changed, `"noop"` when the new value equals the current axis (so the caller
 * skips it). The Z axis defaults the existing value to 0 (`drawingPointOptionalZSchema`
 * + export `z ?? 0`) so an identical-Z edit is a no-op and a real Z change
 * registers (residual R2 #317 — group-30 Z editable).
 */
function applyCenterAxisChange(
  center: { x: number; y: number; z?: number },
  name: "Center X" | "Center Y" | "Center Z",
  parsed: number
): "applied" | "noop" {
  if (name === "Center X") {
    if (center.x === parsed) return "noop";
    center.x = parsed;
  } else if (name === "Center Y") {
    if (center.y === parsed) return "noop";
    center.y = parsed;
  } else {
    if ((center.z ?? 0) === parsed) return "noop";
    center.z = parsed;
  }
  return "applied";
}

/**
 * The shared per-change outcome of the CIRCLE/ARC property loop. `applied` /
 * `noop` mirror the per-axis mutation result; `skip` carries the reason the
 * caller pushes into `skipped`; `unhandled` means the change is not a
 * center/radius property (an ARC Start/End angle the caller handles itself).
 */
type CircleArcCenterRadiusOutcome =
  | { kind: "applied" }
  | { kind: "noop" }
  | { kind: "skip"; reason: string }
  | { kind: "unhandled"; parsed: number };

/**
 * Process the CENTER + RADIUS portion of a CIRCLE/ARC property change, mutating
 * `center` / the radius slot in place. This is the one-truth shared by
 * `applyCirclePropertyChanges` / `applyArcPropertyChanges` — sibling
 * discriminated-union handlers whose radius-validation + numeric-parse + center
 * X/Y/Z (incl. the Z-editable group-30 contract, residual R2 #317) block was
 * byte-identical. The caller owns `appliedChanges` / `skipped` accumulation and,
 * for ARC, the Start/End-angle handling (returned as `unhandled` with the parsed
 * numeric so the caller need not re-parse).
 *
 * `setRadius` is the radius writer (a closure over the caller's `next.radius`)
 * so the helper stays entity-shape-agnostic.
 */
function applyCenterRadiusChange(
  change: SimpleGeometryPropertyChange,
  center: { x: number; y: number; z?: number },
  currentRadius: number,
  setRadius: (value: number) => void
): CircleArcCenterRadiusOutcome {
  if (change.name === "Radius") {
    const parsedRadius = parsePositiveNumber(change.value);
    if (parsedRadius === null) {
      return { kind: "skip", reason: "radius must be a positive number" };
    }
    if (currentRadius === parsedRadius) {
      return { kind: "noop" };
    }
    setRadius(parsedRadius);
    return { kind: "applied" };
  }

  const parsed = parseStrictNumber(change.value);
  if (parsed === null) {
    return { kind: "skip", reason: "property value must be numeric" };
  }

  if (
    change.name === "Center X" ||
    change.name === "Center Y" ||
    change.name === "Center Z"
  ) {
    return { kind: applyCenterAxisChange(center, change.name, parsed) };
  }

  // Not a center/radius property — the caller (ARC) handles Start/End angle with
  // the already-parsed numeric.
  return { kind: "unhandled", parsed };
}

/**
 * Drive the CENTER + RADIUS portion of the CIRCLE/ARC property loop, the
 * one-truth shared by `applyCirclePropertyChanges` / `applyArcPropertyChanges`.
 * For every change it runs the patchable-property skip, calls
 * `applyCenterRadiusChange` (mutating `next.center` / `next.radius` in place),
 * and folds the per-change outcome:
 *  - `skip`  → pushed into `skipped` with its reason,
 *  - `noop`  → dropped (no center/radius value actually changed),
 *  - `applied` → pushed into `applied`,
 *  - `unhandled` → collected (with its parsed numeric) into `unhandled` and NOT
 *    pushed to `applied` — the CALLER decides (an ARC owns Start/End angle; a
 *    CIRCLE has no such property so its `unhandled` is always empty).
 *
 * The accumulators are returned so the caller keeps the exact applied/skipped
 * semantics it had inline.
 */
function applyCenterRadiusMutations(
  entityType: "Circle" | "Arc",
  entityId: string,
  changes: SimpleGeometryPropertyChange[],
  next: { center: SimpleGeometryPointLike; radius: number }
): {
  applied: SimpleGeometryPropertyChange[];
  skipped: SimpleGeometryPropertySkip[];
  unhandled: Array<{ change: SimpleGeometryPropertyChange; parsed: number }>;
} {
  const applied: SimpleGeometryPropertyChange[] = [];
  const skipped: SimpleGeometryPropertySkip[] = [];
  const unhandled: Array<{ change: SimpleGeometryPropertyChange; parsed: number }> = [];

  for (const change of changes) {
    if (!isSimpleGeometryPatchableProperty(entityType, change.name)) {
      // fix/properties-apply-derived-fields-skip — a CIRCLE Diameter/
      // Circumference/Area or ARC Total angle/Arc length row is resubmitted
      // unedited on EVERY Properties-panel apply (the panel drafts the whole
      // row set, not just the touched field); classify it "derived" so it
      // never inflates the "skipped N incompatible" audit count. A property
      // that genuinely does not belong to this entity type stays
      // "incompatible", counted exactly as before.
      const derived = isDerivedDisplayProperty(entityType, change.name);
      skipped.push({
        entityId,
        property: change.name,
        reason: derived
          ? `property is read-only for ${entityType}`
          : `${change.name} does not apply to ${entityType}`,
        kind: derived ? "derived" : "incompatible"
      });
      continue;
    }

    // Center X/Y/Z + Radius share the one mutation truth across CIRCLE/ARC (incl.
    // the Z-editable group-30 contract, residual R2 #317) via
    // `applyCenterRadiusChange`. A CIRCLE has no non-center/radius patchable
    // property, so its `unhandled` is always empty (the patchable check above
    // already filtered every other label); an ARC's Start/End angle arrives as
    // `unhandled` with the parsed numeric for the caller to apply.
    const outcome = applyCenterRadiusChange(
      change,
      next.center,
      next.radius,
      (value) => {
        next.radius = value;
      }
    );
    if (outcome.kind === "skip") {
      // A VALUE-validation failure (non-numeric / non-positive radius) on an
      // otherwise-patchable property — always a genuine incompatibility, not
      // a derived-field matter (the property IS settable, this submission
      // just failed).
      skipped.push({ entityId, property: change.name, reason: outcome.reason, kind: "incompatible" });
      continue;
    }
    if (outcome.kind === "noop") {
      continue;
    }
    if (outcome.kind === "unhandled") {
      unhandled.push({ change, parsed: outcome.parsed });
      continue;
    }

    applied.push(change);
  }

  return { applied, skipped, unhandled };
}

/**
 * Emit the CENTER + RADIUS portion of a CIRCLE/ARC patch — the one-truth shared
 * by `applyCirclePropertyChanges` / `applyArcPropertyChanges`.
 *
 * Re-emit the FULL center whenever it changed. `center` is shallow-merged by
 * `patchEntityById`, so emitting only x/y would overwrite `{x,y,z}` with `{x,y}`
 * and flatten a non-zero elevation on a 2-D edit (residual R2 #317 — Z-preserve).
 * `withOptionalZ` carries a non-zero Z verbatim and OMITS a z of 0 so a planar
 * circle/arc keeps its bare `{x,y}` shape (the model field is optional, export
 * does `z ?? 0`) — mirror of the LINE start/end contract. The radius is emitted
 * only when it actually changed.
 */
function buildCenterRadiusPatchChanges(
  current: { center: SimpleGeometryPointLike; radius: number },
  next: { center: SimpleGeometryPointLike; radius: number }
): { center?: SimpleGeometryPointLike; radius?: number } {
  const changes: { center?: SimpleGeometryPointLike; radius?: number } = {};

  if (!isPointEqualXYZ(current.center, next.center)) {
    changes.center = withOptionalZ(next.center.x, next.center.y, next.center.z);
  }

  if (current.radius !== next.radius) {
    changes.radius = next.radius;
  }

  return changes;
}

function applyLinePropertyChanges(
  entity: EntityPreview,
  changes: SimpleGeometryPropertyChange[]
): SimpleGeometryPropertyChangeResult {
  const current = readLineState(entity.properties);
  const next = {
    start: { ...current.start },
    end: { ...current.end }
  };
  const appliedChanges: SimpleGeometryPropertyChange[] = [];
  const skipped: SimpleGeometryPropertySkip[] = [];

  for (const change of changes) {
    if (!isSimpleGeometryPatchableProperty("Line", change.name)) {
      // fix/properties-apply-derived-fields-skip — Length/Angle are
      // resubmitted unedited on EVERY Properties-panel apply (the panel
      // drafts the whole row set, not just the touched field); classify
      // them "derived" so they never inflate the "skipped N incompatible"
      // audit count. A property that genuinely does not belong to Line
      // stays "incompatible", counted exactly as before.
      const derived = isDerivedDisplayProperty("Line", change.name);
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason: derived
          ? "property is read-only for Line"
          : `${change.name} does not apply to Line`,
        kind: derived ? "derived" : "incompatible"
      });
      continue;
    }

    const parsed = parseStrictNumber(change.value);

    if (parsed === null) {
      // A VALUE-validation failure on an already-patchable Line property —
      // always a genuine incompatibility (see the "skip" outcome note above).
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason: "property value must be numeric",
        kind: "incompatible"
      });
      continue;
    }

    switch (change.name) {
      case "Start X":
        if (next.start.x === parsed) {
          continue;
        }
        next.start.x = parsed;
        break;
      case "Start Y":
        if (next.start.y === parsed) {
          continue;
        }
        next.start.y = parsed;
        break;
      case "Start Z":
        if (next.start.z === parsed) {
          continue;
        }
        next.start.z = parsed;
        break;
      case "End X":
        if (next.end.x === parsed) {
          continue;
        }
        next.end.x = parsed;
        break;
      case "End Y":
        if (next.end.y === parsed) {
          continue;
        }
        next.end.y = parsed;
        break;
      case "End Z":
        if (next.end.z === parsed) {
          continue;
        }
        next.end.z = parsed;
        break;
    }

    appliedChanges.push(change);
  }

  const hasChanges = !isPointEqualXYZ(current.start, next.start) || !isPointEqualXYZ(current.end, next.end);
  const nextEntity = hasChanges
    ? buildSimpleGeometryPreviewEntity(
        {
          id: entity.id,
          type: "Line",
          spaceId: entity.spaceId,
          start: next.start,
          end: next.end
        },
        {
          label: entity.label,
          summary: entity.summary,
          properties: entity.properties
        }
      )
    : entity;

  const patch = hasChanges
    ? (() => {
        const changes: Extract<SimpleGeometryPatch, { type: "Line" }>["changes"] = {};

        // Re-emit the FULL endpoint whenever it changed. `start`/`end` are
        // shallow-merged by `patchEntityById`, so emitting only x/y would
        // overwrite `{x,y,z}` with `{x,y}` and flatten a non-zero elevation on a
        // 2-D edit (residual R2 #317 — Z-preserve). `next.*.z` carries the
        // existing Z that `readLineState` threaded through.
        //
        // A NON-ZERO Z is re-emitted (preserved). A z of 0 is OMITTED so a 2-D
        // line keeps its bare `{x,y}` shape — the model field is optional
        // (drawingPointOptionalZSchema) and export does `z ?? 0`, so absent z
        // and z:0 are the SAME elevation; emitting `z:0` would only add a
        // spurious field and break a byte-identical round-trip for planar lines.
        if (!isPointEqualXYZ(current.start, next.start)) {
          changes.start = withOptionalZ(next.start.x, next.start.y, next.start.z);
        }

        if (!isPointEqualXYZ(current.end, next.end)) {
          changes.end = withOptionalZ(next.end.x, next.end.y, next.end.z);
        }

        return {
          entityId: entity.id,
          type: "Line" as const,
          changes
        };
      })()
    : null;

  return {
    entity: nextEntity,
    patch,
    appliedChanges,
    skipped,
    summary: createSummary("Line", hasChanges)
  };
}

function applyCirclePropertyChanges(
  entity: EntityPreview,
  changes: SimpleGeometryPropertyChange[]
): SimpleGeometryPropertyChangeResult {
  const current = readCircleState(entity.properties);
  const next = {
    center: { ...current.center },
    radius: current.radius
  };

  // Center X/Y/Z + Radius mutation + skip/noop accumulation is the one truth
  // shared with ARC (incl. the Z-editable group-30 contract, residual R2 #317).
  // A CIRCLE has no non-center/radius patchable property, so `unhandled` is
  // always empty here (the patchable check inside already filtered every other
  // label) — there is no caller-owned tail to apply.
  const { applied: appliedChanges, skipped } = applyCenterRadiusMutations(
    "Circle",
    entity.id,
    changes,
    next
  );

  const hasChanges =
    !isPointEqualXYZ(current.center, next.center) || current.radius !== next.radius;
  const nextEntity = hasChanges
    ? buildSimpleGeometryPreviewEntity(
        {
          id: entity.id,
          type: "Circle",
          spaceId: entity.spaceId,
          center: next.center,
          radius: next.radius
        },
        {
          label: entity.label,
          summary: entity.summary,
          properties: entity.properties
        }
      )
    : entity;

  const patch = hasChanges
    ? (() => {
        // Center (Z-preserve, residual R2 #317) + radius are the one-truth shared
        // with ARC via `buildCenterRadiusPatchChanges`; a CIRCLE has no further
        // patch field.
        const changes: Extract<SimpleGeometryPatch, { type: "Circle" }>["changes"] = {
          ...buildCenterRadiusPatchChanges(current, next)
        };

        return {
          entityId: entity.id,
          type: "Circle" as const,
          changes
        };
      })()
    : null;

  return {
    entity: nextEntity,
    patch,
    appliedChanges,
    skipped,
    summary: createSummary("Circle", hasChanges)
  };
}

function applyArcPropertyChanges(
  entity: EntityPreview,
  changes: SimpleGeometryPropertyChange[]
): SimpleGeometryPropertyChangeResult {
  const current = readArcState(entity.properties);
  const next = {
    center: { ...current.center },
    radius: current.radius,
    startAngle: current.startAngle,
    endAngle: current.endAngle
  };

  // Center X/Y/Z + Radius mutation + skip/noop accumulation is the one truth
  // shared with CIRCLE (incl. the Z-editable group-30 contract, residual R2
  // #317). The ARC-only Start/End angle arrive as `unhandled` with the parsed
  // numeric so this caller applies them without re-parsing.
  const { applied: appliedChanges, skipped, unhandled } = applyCenterRadiusMutations(
    "Arc",
    entity.id,
    changes,
    next
  );

  for (const { change, parsed } of unhandled) {
    if (change.name === "Start angle") {
      if (next.startAngle === parsed) {
        continue;
      }
      next.startAngle = parsed;
    } else if (change.name === "End angle") {
      if (next.endAngle === parsed) {
        continue;
      }
      next.endAngle = parsed;
    } else {
      continue;
    }

    appliedChanges.push(change);
  }

  const hasChanges =
    !isPointEqualXYZ(current.center, next.center) ||
    current.radius !== next.radius ||
    current.startAngle !== next.startAngle ||
    current.endAngle !== next.endAngle;
  const nextEntity = hasChanges
    ? buildSimpleGeometryPreviewEntity(
        {
          id: entity.id,
          type: "Arc",
          spaceId: entity.spaceId,
          center: next.center,
          radius: next.radius,
          startAngle: next.startAngle,
          endAngle: next.endAngle
        },
        {
          label: entity.label,
          summary: entity.summary,
          properties: entity.properties
        }
      )
    : entity;

  const patch = hasChanges
    ? (() => {
        // Center (Z-preserve, residual R2 #317 — mirror of CIRCLE / LINE) + radius
        // are the one-truth shared with CIRCLE via `buildCenterRadiusPatchChanges`;
        // the ARC-only Start/End angle are appended below.
        const changes: Extract<SimpleGeometryPatch, { type: "Arc" }>["changes"] = {
          ...buildCenterRadiusPatchChanges(current, next)
        };

        if (current.startAngle !== next.startAngle) {
          changes.startAngle = next.startAngle;
        }

        if (current.endAngle !== next.endAngle) {
          changes.endAngle = next.endAngle;
        }

        return {
          entityId: entity.id,
          type: "Arc" as const,
          changes
        };
      })()
    : null;

  return {
    entity: nextEntity,
    patch,
    appliedChanges,
    skipped,
    summary: createSummary("Arc", hasChanges)
  };
}

export function applySimpleGeometryPropertyChanges(
  entity: EntityPreview,
  changes: SimpleGeometryPropertyChange[]
): SimpleGeometryPropertyChangeResult {
  if (!isSimpleGeometryEntity(entity)) {
    return {
      entity,
      patch: null,
      appliedChanges: [],
      skipped: changes.map((change) => ({
        entityId: entity.id,
        property: change.name,
        reason: "unsupported-entity",
        kind: "incompatible" as const
      })),
      summary: "Geometry properties applied to unsupported entity"
    };
  }

  switch (entity.type) {
    case "Line":
      return applyLinePropertyChanges(entity, changes);
    case "Circle":
      return applyCirclePropertyChanges(entity, changes);
    case "Arc":
      return applyArcPropertyChanges(entity, changes);
  }
}

// `extendedPatchableLookup` and `isExtendedPatchableProperty` were
// hoisted into `previewBuilders.ts` so `extendedPropertyMutations.ts`
// can pull them in without re-importing this module (was a depcruise
// cycle source). Re-exported here for back-compat.
export {
  extendedPatchableLookup,
  isExtendedPatchableProperty,
} from "./previewBuilders";

// Computed-property calculators (computePolylineLength /
// computePolylineArea) moved to `geometryEntityTypes.ts` so previewBuilders
// can use them without back-importing simpleGeometry. Re-exported above.

// Re-export from sub-modules for barrel access
export * from "./previewBuilders";
export * from "./extendedPropertyMutations";
